/**
 * MachineryTypes — мини-справочник «Типы техники» (AK-235).
 *
 * Тип привязан к группе (machinery_group): трактор/комбайн в самоходной,
 * плуг/культиватор в прицепной, агродрон в авиа. Inline-таблица с фильтром по
 * группе и выбором группы в строке создания. DELETE-guard через usage_count.
 */
import { useMemo, useState } from 'react';
import { Check, Lock, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PlatformAdminLayout } from '@/layouts/PlatformAdminLayout';
import {
  useCreateMachineryType,
  useDeleteMachineryType,
  useMachineryGroups,
  useMachineryTypes,
  useUpdateMachineryType,
} from './hooks/useMachineryQueries';
import {
  machineryGroupLabel,
  type MachineryType,
  type MachineryTypePayload,
} from './machineryCatalogTypes';
import { describeApiError } from '@/lib/apiError';
import { toast } from '@/lib/toast';

interface Draft {
  machinery_group: string;
  code: string;
  ru: string;
}

const emptyDraft = (group: string): Draft => ({ machinery_group: group, code: '', ru: '' });

function draftToPayload(d: Draft): MachineryTypePayload {
  return {
    machinery_group: d.machinery_group,
    code: d.code.trim(),
    name_translations: d.ru.trim() ? { ru: d.ru.trim() } : {},
  };
}

const draftValid = (d: Draft): boolean =>
  /^[a-z0-9_]+$/.test(d.code.trim()) && d.ru.trim().length > 0 && Boolean(d.machinery_group);

export function PlatformAdminMachineryTypesPage() {
  const groupsQ = useMachineryGroups();
  const groups = groupsQ.data?.items ?? [];
  const [groupFilter, setGroupFilter] = useState<string>('all');

  const typesQ = useMachineryTypes(groupFilter);
  const createMut = useCreateMachineryType();
  const updateMut = useUpdateMachineryType();
  const deleteMut = useDeleteMachineryType();

  const items = typesQ.data?.items ?? [];
  const defaultGroup = groups[0]?.code ?? 'self_propelled';

  const [creating, setCreating] = useState<Draft | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState<Draft>(emptyDraft(defaultGroup));

  const groupOptions = useMemo(
    () => groups.map((g) => ({ value: g.code, label: g.name_translations.ru ?? g.code })),
    [groups],
  );

  const startCreate = () =>
    setCreating(emptyDraft(groupFilter !== 'all' ? groupFilter : defaultGroup));
  const startEdit = (t: MachineryType) => {
    setEditingId(t.id);
    setEditDraft({
      machinery_group: t.machinery_group,
      code: t.code,
      ru: t.name_translations.ru ?? '',
    });
  };

  const saveCreate = async () => {
    if (!creating || !draftValid(creating)) return;
    // AK-574: отказ сервера показываем, а не роняем в unhandled rejection.
    try {
      await createMut.mutateAsync(draftToPayload(creating));
      toast.success('Тип техники добавлен');
      setCreating(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  };

  const saveEdit = async () => {
    if (!editingId || !draftValid(editDraft)) return;
    try {
      await updateMut.mutateAsync({ id: editingId, payload: draftToPayload(editDraft) });
      toast.success('Тип техники обновлён');
      setEditingId(null);
    } catch (err) {
      toast.error(describeApiError(err));
    }
  };

  const handleDelete = async (t: MachineryType) => {
    if (t.usage_count > 0) return;
    if (!window.confirm(`Удалить «${t.name_translations.ru ?? t.code}»?`)) return;
    try {
      await deleteMut.mutateAsync(t.id);
      toast.success('Тип удалён');
    } catch (err) {
      toast.error(describeApiError(err));
    }
  };

  return (
    <PlatformAdminLayout>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Типы техники</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Мини-справочник типов внутри групп · {items.length} записей
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={groupFilter} onValueChange={setGroupFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все группы</SelectItem>
              {groupOptions.map((g) => (
                <SelectItem key={g.value} value={g.value}>
                  {g.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={startCreate} disabled={Boolean(creating)}>
            <Plus className="h-4 w-4" />
            Добавить тип
          </Button>
        </div>
      </div>

      <Card className="mt-5">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead style={{ width: '200px' }}>Группа</TableHead>
                <TableHead style={{ width: '180px' }}>Код</TableHead>
                <TableHead>Название (RU)</TableHead>
                <TableHead style={{ width: '150px' }}>Использование</TableHead>
                <TableHead style={{ width: '110px' }} className="text-right">
                  Действия
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {typesQ.isLoading &&
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {Array.from({ length: 5 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton className="h-4 w-20" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {creating && (
                <TableRow>
                  <TableCell>
                    <Select
                      value={creating.machinery_group}
                      onValueChange={(v) => setCreating({ ...creating, machinery_group: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {groupOptions.map((g) => (
                          <SelectItem key={g.value} value={g.value}>
                            {g.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Input
                      value={creating.code}
                      onChange={(e) => setCreating({ ...creating, code: e.target.value })}
                      placeholder="tractor"
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={creating.ru}
                      onChange={(e) => setCreating({ ...creating, ru: e.target.value })}
                      placeholder="Трактор"
                    />
                  </TableCell>
                  <TableCell>—</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        onClick={() => void saveCreate()}
                        disabled={!draftValid(creating)}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setCreating(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}

              {items.map((t) =>
                editingId === t.id ? (
                  <TableRow key={t.id}>
                    <TableCell>
                      <Select
                        value={editDraft.machinery_group}
                        onValueChange={(v) => setEditDraft({ ...editDraft, machinery_group: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {groupOptions.map((g) => (
                            <SelectItem key={g.value} value={g.value}>
                              {g.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        value={editDraft.code}
                        onChange={(e) => setEditDraft({ ...editDraft, code: e.target.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        value={editDraft.ru}
                        onChange={(e) => setEditDraft({ ...editDraft, ru: e.target.value })}
                      />
                    </TableCell>
                    <TableCell>—</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          onClick={() => void saveEdit()}
                          disabled={!draftValid(editDraft)}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow key={t.id}>
                    <TableCell>
                      <Badge variant="outline">{machineryGroupLabel(t.machinery_group)}</Badge>
                    </TableCell>
                    <TableCell>
                      <code className="text-xs text-muted-foreground">{t.code}</code>
                    </TableCell>
                    <TableCell className="font-medium text-foreground">
                      {t.name_translations.ru ?? t.code}
                    </TableCell>
                    <TableCell>
                      {t.usage_count > 0 ? (
                        <Badge variant="secondary">{t.usage_count} моделей</Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => startEdit(t)}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {t.usage_count > 0 ? (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled
                            title="Используется моделями техники — нельзя удалить"
                          >
                            <Lock className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => void handleDelete(t)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ),
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </PlatformAdminLayout>
  );
}

export default PlatformAdminMachineryTypesPage;
